// Innermost pipe-bearing block, e.g. `{a|b|c}` — never a variable token, whose
// grammar (`{[A-Za-z_][A-Za-z0-9_]*}`) forbids the pipe. The `[^{}]` class keeps
// the match innermost so nesting resolves inside-out.
const SPINTAX_BLOCK = /\{([^{}]*\|[^{}]*)\}/

/**
 * Deterministic RNG from an integer seed (mulberry32), so a spintax preview is
 * stable across re-renders and re-rolls only when the seed changes.
 */
export function spintaxRng(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Whether the text contains at least one spintax block. */
export function containsSpintax(text: string): boolean {
  return SPINTAX_BLOCK.test(text)
}

/**
 * Resolve spintax `{a|b|c}` blocks to one randomly chosen option each, display
 * only. Recurses innermost-first so `{a {b|c}|d}` collapses correctly. Leaves
 * pipe-less `{VARIABLE}` tokens and unbalanced braces untouched.
 *
 * A `{VARIABLE}` embedded inside a spin option (e.g. `{Hi {COMPANY}|Hey}`) is
 * not recognized as spintax here: the pipe sits outside the inner braces, so
 * the block is left untouched and only the inner variable fills, showing a
 * literal `{Hi Acme|Hey}` in the preview. This is preview-only. EmailBison
 * resolves the nested spintax + variable combination per recipient at send
 * time, so the actual email is unaffected.
 */
export function resolveSpintax(text: string, rng: () => number): string {
  let result = text
  let match: RegExpExecArray | null
  while ((match = SPINTAX_BLOCK.exec(result)) !== null) {
    const options = match[1].split('|')
    const pick = options[Math.floor(rng() * options.length)]
    result =
      result.slice(0, match.index) +
      pick +
      result.slice(match.index + match[0].length)
  }
  return result
}
